import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useUserInfo from '@src/hooks/useTtsUser';
import { FEATURE_KEYS } from '@src/configs/featureKeys';

const useBGMPolicyAgreement = () => {
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const { ttsUser } = useUserInfo();
  const user = useSelector((state) => state.auth.user);
  const ttsUserRaw = useSelector((state) => state.user.ttsUser);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPolicyFeatureOn = getFeatureValue(
    FEATURE_KEYS.BACKGROUND_MUSIC_POLICY_AGREEMENT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const isBgmTermAgreed = Boolean(ttsUser?.bgmTermAgreedAt);

  const shouldRequireBgmPolicyAgreement = ({ isFeatureOn, isAgreed }) =>
    Boolean(isFeatureOn) && !isAgreed;
  const isPolicyAccepted = isBgmTermAgreed;
  const needPolicyAgreement = shouldRequireBgmPolicyAgreement({
    isFeatureOn: isPolicyFeatureOn,
    isAgreed: isPolicyAccepted,
  });

  // Calls the agree API and optimistically merges the timestamp into ttsUser so
  // `needPolicyAgreement` flips to false on the next render. Returns success.
  const agreePolicy = async () => {
    try {
      setIsSubmitting(true);
      const response = await apis.user.agreeBackgroundMusicTerm();
      if (!response?.status) throw new Error('agree-failed');
      dispatch(
        actions.user.getTtsUserSuccess({
          ...ttsUserRaw,
          bgmTermAgreedAt:
            response?.result?.bgmTermAgreedAt || new Date().toISOString(),
        }),
      );
      return true;
    } catch (error) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'errorOccurred' }),
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isPolicyFeatureOn,
    isPolicyAccepted,
    needPolicyAgreement,
    isSubmitting,
    agreePolicy,
  };
};

export default useBGMPolicyAgreement;
